/**
 * Restrizioni di visibilità sui lead definite nei RUOLI (roles.visibility_restrictions.leads).
 *
 * Sono opzionali e configurabili dall'editor dei ruoli: pipeline consentite,
 * fonti consentite, attribuzione pubblicitaria consentita. Chiave assente o
 * lista vuota = nessuna restrizione su quell'asse.
 *
 * Semantica:
 *  - pipeline_ids: il lead è visibile solo se la sua pipeline è tra quelle elencate
 *    (i lead senza pipeline, "legacy", restano fuori);
 *  - lead_source_ids / ad_platforms: il lead è visibile se la sua fonte è tra quelle
 *    elencate OPPURE la sua attribuzione pubblicitaria (clients.ad_platform) è tra le
 *    piattaforme elencate. I due assi sono in OR tra loro perché un lead Meta può avere
 *    la fonte "Meta Ads" o solo l'attribuzione ad_platform=meta (o entrambe).
 *
 * Più ruoli sullo stesso utente: se almeno un ruolo non limita un asse, l'utente non è
 * limitato su quell'asse; altrimenti vale l'unione delle liste (mergeLeadVisibility).
 *
 * È un filtro di vista applicato lato app (store dei lead, kanban, ricerca, dettaglio,
 * realtime), sullo stesso piano dello scope "solo i propri" (filter_by_user).
 */

export const AD_PLATFORM_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'meta', label: 'Meta (Facebook / Instagram)' },
  { id: 'google', label: 'Google Ads' },
  { id: 'linkedin', label: 'LinkedIn Ads' },
];

export interface LeadVisibilityRestrictions {
  pipeline_ids?: string[];
  lead_source_ids?: string[];
  ad_platforms?: string[];
}

export interface RoleVisibilityRestrictions {
  leads?: LeadVisibilityRestrictions;
}

/** Restrizione effettiva dell'utente: null = nessuna restrizione. */
export interface LeadVisibility {
  pipelineIds: string[] | null;    // null = tutte le pipeline
  leadSourceIds: string[] | null;  // null = tutte le fonti (insieme ad adPlatforms)
  adPlatforms: string[] | null;
}

const cleanList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length > 0) : [];

/** True se il JSON del ruolo contiene almeno una restrizione reale sui lead. */
export function roleHasLeadRestrictions(r: RoleVisibilityRestrictions | null | undefined): boolean {
  const l = r?.leads;
  if (!l) return false;
  return cleanList(l.pipeline_ids).length > 0
    || cleanList(l.lead_source_ids).length > 0
    || cleanList(l.ad_platforms).length > 0;
}

/**
 * Fonde le restrizioni di tutti i ruoli dell'utente.
 * Un asse è limitato solo se TUTTI i ruoli lo limitano; in quel caso unione delle liste.
 * Le fonti e le piattaforme formano un unico asse (OR), quindi si valutano insieme.
 */
export function mergeLeadVisibility(
  roleRestrictions: Array<RoleVisibilityRestrictions | null | undefined>
): LeadVisibility | null {
  if (roleRestrictions.length === 0) return null;

  const leads = roleRestrictions.map((r) => r?.leads || {});

  const everyRolePipelines = leads.every((l) => cleanList(l.pipeline_ids).length > 0);
  const everyRoleSources = leads.every(
    (l) => cleanList(l.lead_source_ids).length > 0 || cleanList(l.ad_platforms).length > 0
  );

  const pipelineIds = everyRolePipelines
    ? Array.from(new Set(leads.flatMap((l) => cleanList(l.pipeline_ids))))
    : null;
  const leadSourceIds = everyRoleSources
    ? Array.from(new Set(leads.flatMap((l) => cleanList(l.lead_source_ids))))
    : null;
  const adPlatforms = everyRoleSources
    ? Array.from(new Set(leads.flatMap((l) => cleanList(l.ad_platforms))))
    : null;

  if (!pipelineIds && !leadSourceIds && !adPlatforms) return null;
  return { pipelineIds, leadSourceIds, adPlatforms };
}

/** Il lead (riga di clients) rientra nella visibilità? */
export function isLeadVisible(
  client: { pipeline_id?: string | null; lead_source?: string | null; ad_platform?: string | null } | null | undefined,
  v: LeadVisibility | null
): boolean {
  if (!v || !client) return true;
  if (v.pipelineIds && !(client.pipeline_id && v.pipelineIds.includes(client.pipeline_id))) return false;
  if (v.leadSourceIds || v.adPlatforms) {
    const bySource = !!(client.lead_source && v.leadSourceIds?.includes(client.lead_source));
    const byPlatform = !!(client.ad_platform && v.adPlatforms?.includes(client.ad_platform));
    if (!bySource && !byPlatform) return false;
  }
  return true;
}

/** La pipeline (id) è tra quelle consentite? 'all' e 'legacy' seguono le regole sopra. */
export function isPipelineVisible(pipelineId: string | null | undefined, v: LeadVisibility | null): boolean {
  if (!v || !v.pipelineIds) return true;
  if (!pipelineId || pipelineId === 'legacy') return false;
  if (pipelineId === 'all') return true;
  return v.pipelineIds.includes(pipelineId);
}

/**
 * Condizione PostgREST (sintassi .or()) per fonte/attribuzione, da usare con
 * `query.or(cond)`. Null se l'asse non è limitato. Gli id sono uuid/parole
 * senza virgole o parentesi, quindi non serve quoting.
 */
export function leadSourceOrCondition(v: LeadVisibility | null): string | null {
  if (!v || (!v.leadSourceIds && !v.adPlatforms)) return null;
  const parts: string[] = [];
  if (v.leadSourceIds && v.leadSourceIds.length > 0) parts.push(`lead_source.in.(${v.leadSourceIds.join(',')})`);
  if (v.adPlatforms && v.adPlatforms.length > 0) parts.push(`ad_platform.in.(${v.adPlatforms.join(',')})`);
  // Asse limitato ma liste vuote (non dovrebbe succedere): nessun lead visibile.
  if (parts.length === 0) return 'id.eq.00000000-0000-0000-0000-000000000000';
  return parts.join(',');
}

/** Applica la restrizione a una query supabase-js su `clients` (builder mutabile). */
export function applyLeadVisibilityToQuery<T extends { in: (col: string, v: string[]) => T; or: (c: string) => T }>(
  query: T,
  v: LeadVisibility | null
): T {
  if (!v) return query;
  let q = query;
  if (v.pipelineIds) {
    q = q.in('pipeline_id', v.pipelineIds.length > 0 ? v.pipelineIds : ['00000000-0000-0000-0000-000000000000']);
  }
  const cond = leadSourceOrCondition(v);
  if (cond) q = q.or(cond);
  return q;
}

/** Messaggio mostrato quando si apre un lead fuori dalla visibilità del ruolo. */
export const LEAD_NOT_VISIBLE_MESSAGE = 'Questo lead non rientra nella visibilità del tuo ruolo';
